import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60 * 1000; // 60 seconds
const STORAGE_KEY = 'ssn-last-breaking-check';

/**
 * Polls the backend for new breaking news since last check.
 * Shows in-app toast notifications.
 */
export default function useBreakingNewsPoller(enabled = true) {
  const navigate = useNavigate();
  const seenIdsRef = useRef(new Set());
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    // Load already-seen IDs from session storage
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        seenIdsRef.current = new Set(JSON.parse(stored));
      } catch (e) {
        seenIdsRef.current = new Set();
      }
    }

    const checkBreakingNews = async () => {
      try {
        const response = await axios.get(`${API}/news?breaking=true&limit=5`);
        const items = response.data || [];

        // Filter items published in the last 2 hours to avoid stale
        const cutoff = Date.now() - 2 * 60 * 60 * 1000;
        const fresh = items.filter((n) => {
          const t = new Date(n.published_at).getTime();
          return t >= cutoff;
        });

        // Find items we haven't seen yet
        const newItems = fresh.filter((n) => !seenIdsRef.current.has(n.id));

        // On first run, just mark all as seen (don't show toasts for existing)
        if (seenIdsRef.current.size === 0) {
          items.forEach((n) => seenIdsRef.current.add(n.id));
          sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(Array.from(seenIdsRef.current))
          );
          return;
        }

        // Show toast for each new breaking news (max 2 at a time)
        newItems.slice(0, 2).forEach((news) => {
          toast(`🚨 BREAKING: ${news.title}`, {
            description: news.summary.substring(0, 120) + '...',
            duration: 8000,
            action: {
              label: 'Öffnen',
              onClick: () => navigate(`/news/${news.id}`),
            },
          });
          seenIdsRef.current.add(news.id);
        });

        // Update storage
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(Array.from(seenIdsRef.current).slice(-100))
        );
      } catch (err) {
        // Silent fail - don't spam console
      }
    };

    // Initial check
    checkBreakingNews();

    // Poll interval
    intervalRef.current = setInterval(checkBreakingNews, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // API/POLL_INTERVAL_MS/STORAGE_KEY are module-level constants and cannot change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, navigate]);
}
